/**
 * Unified Cache API for Streameater
 * Combines memory cache and localStorage with fallback chain
 */

import { TTL, CACHE_KEYS, PRIORITY_DELAYS } from "./cacheConfig";
import type { FetchPriority } from "./cacheConfig";
import {
  getFromStorage,
  setInStorage,
  getStaleFromStorage,
  clearStorageCache,
  clearStoragePattern,
  getStorageStats,
  cleanup as cleanupStorage,
} from "./localStorage";
import { memoryCache, getStaleFromMemory } from "./memoryCache";

export { TTL, CACHE_KEYS, PRIORITY_DELAYS };
export type { FetchPriority };

interface CacheResult<T> {
  data: T;
  source: "memory" | "storage" | "network";
  isStale: boolean;
}

/**
 * Get data from cache with fallback chain:
 * 1. Memory cache (fastest)
 * 2. LocalStorage (persistent)
 * 3. Return null (needs network fetch)
 */
export const getFromCache = <T>(key: string): CacheResult<T> | null => {
  // Try memory first (fastest)
  const memoryResult = memoryCache.get<T>(key);
  if (memoryResult !== null) {
    return { data: memoryResult, source: "memory", isStale: false };
  }

  // Try localStorage (persistent)
  const storageResult = getFromStorage<T>(key);
  if (storageResult !== null) {
    // Populate memory cache for faster subsequent access
    memoryCache.set(key, storageResult, TTL.HOME_DATA);
    return { data: storageResult, source: "storage", isStale: false };
  }

  return null;
};

/**
 * Get data from cache, including stale data (for SWR pattern)
 * Returns stale data if available, along with a flag indicating staleness
 */
export const getFromCacheWithStale = <T>(
  key: string,
): CacheResult<T> | null => {
  // Try memory first
  const memoryResult = getStaleFromMemory<T>(key);
  if (memoryResult !== null) {
    return {
      data: memoryResult.data,
      source: "memory",
      isStale: memoryResult.isStale,
    };
  }

  // Try localStorage
  const storageResult = getStaleFromStorage<T>(key);
  if (storageResult !== null) {
    // Populate memory cache
    memoryCache.set(key, storageResult.data, TTL.HOME_DATA);
    return {
      data: storageResult.data,
      source: "storage",
      isStale: storageResult.isStale,
    };
  }

  return null;
};

/**
 * Set data in both caches
 */
export const setInCache = <T>(key: string, data: T, ttl: number): void => {
  // Always set in memory
  memoryCache.set(key, data, ttl);

  // Also persist to localStorage for survival across reloads
  setInStorage(key, data, ttl);
};

/**
 * Invalidate cache for a specific key
 */
export const invalidateCache = (key: string): void => {
  memoryCache.delete(key);
  // Note: We keep localStorage for stale-while-revalidate
};

/**
 * Invalidate all caches matching a pattern
 */
export const invalidateCachePattern = (pattern: string | RegExp): void => {
  memoryCache.clearPattern(pattern);
  clearStoragePattern(pattern);
};

/**
 * Clear all caches
 */
export const clearAllCaches = (): void => {
  memoryCache.clear();
  clearStorageCache();
};

/**
 * Get combined cache statistics
 */
export const getCacheStats = () => {
  const memoryStats = memoryCache.getStats();
  const storageStats = getStorageStats();

  return {
    memory: memoryStats,
    storage: storageStats,
  };
};

/**
 * Perform cache maintenance (cleanup expired entries)
 */
export const performCacheMaintenance = (): void => {
  cleanupStorage();
  // Memory cache handles its own cleanup via LRU
};

/**
 * Fetch with cache - the main utility for data fetching
 */
export interface FetchWithCacheOptions<T> {
  key: string;
  fetcher: () => Promise<T>;
  ttl?: number;
  staleWhileRevalidate?: boolean;
  priority?: FetchPriority;
  onStaleData?: (data: T) => void;
}

export const fetchWithCache = async <T>({
  key,
  fetcher,
  ttl = TTL.HOME_DATA,
  staleWhileRevalidate = true,
  priority = "high",
  onStaleData,
}: FetchWithCacheOptions<T>): Promise<T> => {
  // Check cache first
  const cached = staleWhileRevalidate
    ? getFromCacheWithStale<T>(key)
    : getFromCache<T>(key);

  if (cached) {
    if (!cached.isStale) {
      // Fresh data, return immediately
      return cached.data;
    }

    // Stale data - notify callback and revalidate in background
    if (onStaleData) {
      onStaleData(cached.data);
    }

    // Background revalidation
    setTimeout(async () => {
      try {
        const freshData = await fetcher();
        setInCache(key, freshData, ttl);
      } catch (error) {
        console.warn("[Cache] Background revalidation failed:", key, error);
      }
    }, PRIORITY_DELAYS[priority]);

    return cached.data;
  }

  // No cache, fetch from network
  const data = await fetcher();
  setInCache(key, data, ttl);
  return data;
};

/**
 * Prefetch data into cache without blocking
 */
export const prefetchToCache = <T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number = TTL.HOME_DATA,
): void => {
  // Check if already cached
  const existing = getFromCache<T>(key);
  if (existing) return;

  // Fetch in background
  setTimeout(async () => {
    try {
      const data = await fetcher();
      setInCache(key, data, ttl);
    } catch (error) {
      console.warn("[Cache] Prefetch failed:", key, error);
    }
  }, PRIORITY_DELAYS.low);
};

// Export types
export type { CacheResult };
